<template>
  <div v-if="token" id="token-trade-layout">
    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <!-- NAVIGATION -->
    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <nav class="nav-bar">
      <BackBtn />
      <div class="breadcrumb">
        <router-link :to="{ name: 'Invest' }" class="breadcrumb-link">Invest</router-link>
        <span class="breadcrumb-sep">/</span>
        <span class="breadcrumb-current">{{ capitalizeEachWord(token.name) }}</span>
      </div>
    </nav>

    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <!-- HERO: the deal, and the one thing that can be done with it -->
    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <section class="hero-section">
      <div class="hero-card">
        <div class="hero-content">
          <div class="company-identity">
            <img v-if="logo && !logoFailed" :src="logo" :alt="token.name" class="company-logo" @error="logoFailed = true" />
            <div v-else class="company-avatar">{{ token.name?.charAt(0)?.toUpperCase() }}</div>
            <div class="company-info">
              <h1 class="company-name">{{ capitalizeEachWord(token.name) }}</h1>
              <div class="company-meta">
                <DealTypeBadge v-if="showsKindBadge" :badge="kindBadge" />
                <ShariaBadge :token="token" />
                <!-- What kind of metal or crop, the NGX sector, the fund's category, or the industry. -->
                <span v-if="headerTag" class="industry-badge">{{ headerTag }}</span>
                <span v-if="biddingChip" class="bidding-chip"><i aria-hidden="true"></i>{{ biddingChip }}</span>
                <span v-if="token.country && kind !== 'commodity'" class="country-tag">
                  <svg viewBox="0 0 16 16" fill="none"><path d="M8 8.667a2 2 0 100-4 2 2 0 000 4z" stroke="currentColor" stroke-width="1.2"/><path d="M8 14c2.667-2.667 4-5.054 4-7.167a4 4 0 10-8 0C4 8.946 5.333 11.333 8 14z" stroke="currentColor" stroke-width="1.2"/></svg>
                  {{ token.country }}
                </span>
                <div v-if="asset?.website || asset?.twitter || asset?.facebook" class="social-links">
                  <a v-if="asset?.website" :href="asset.website" target="_blank" rel="noopener" class="social-link" title="Website">
                    <svg viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.2"/><path d="M1.5 8h13M8 2c1.5 1.5 2.5 3.5 2.5 6s-1 4.5-2.5 6c-1.5-1.5-2.5-3.5-2.5-6s1-4.5 2.5-6z" stroke="currentColor" stroke-width="1.2"/></svg>
                  </a>
                  <a v-if="asset?.twitter" :href="asset.twitter" target="_blank" rel="noopener" class="social-link" title="X/Twitter">
                    <svg viewBox="0 0 16 16" fill="none"><path d="M12.6 1.5h2.3l-5 5.8 5.9 7.8h-4.6l-3.7-4.8-4.2 4.8H1l5.4-6.2L.7 1.5h4.8l3.3 4.4 3.8-4.4zm-.8 12.3h1.3L4.4 2.8H3l8.8 11z" fill="currentColor"/></svg>
                  </a>
                  <a v-if="asset?.facebook" :href="asset.facebook" target="_blank" rel="noopener" class="social-link" title="Facebook">
                    <svg viewBox="0 0 16 16" fill="none"><path d="M12 1.333h-2a3.333 3.333 0 00-3.333 3.334v2H4.667v2.666h2v5.334h2.666V9.333h2l.667-2.666h-2.667v-2c0-.369.299-.667.667-.667h2V1.333z" stroke="currentColor" stroke-width="1.2"/></svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div class="hero-actions">
            <!-- Not open: say so where the button was. A countdown, or "Closed, being priced",
                 tells us what is happening; a missing button does not. -->
            <div v-if="tradingHalted" class="inactive-badge">
              <svg viewBox="0 0 16 16" fill="none"><path d="M8 5.333V8M8 10.667h.007M14 8A6 6 0 112 8a6 6 0 0112 0z" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>
              {{ tradingHaltedNote }}
            </div>
            <div v-else-if="canInvest && !offerAcceptsMoney" class="offer-shut">
              {{ offerClosedReason }}
            </div>
            <button v-else-if="canInvest" class="invest-btn primary" @click="openInvestModal('invest')" v-can.disable="'invest:trade'">
              <svg viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
              {{ myLiveBids.length ? 'Place another bid' : isBidding ? 'Place a Bid' : primaryCtaLabel }}
            </button>
            <button v-else-if="canTradeSecondaries" class="invest-btn secondary" @click="openInvestModal('trade shares')" v-can.disable="'invest:trade'">
              <svg viewBox="0 0 16 16" fill="none"><path d="M5 10V3M5 3L2 6M5 3l3 3M11 6v7M11 13l3-3M11 13l-3-3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              {{ byAvailability ? tradeLabel : 'Trade Shares' }}
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <!-- KEY METRICS -->
    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <!-- What matters differs by deal: rate and term on a note, unit price and rate on a fund,
         price and movement on a stock or a metal. trade-view picks the cards. -->
    <section class="metrics-bar">
      <div v-for="card in metricCards" :key="card.label" class="metric-card">
        <span class="metric-label">{{ card.label }}</span>
        <span class="metric-value" :class="card.tone">{{ card.value }} <small v-if="card.unit">{{ card.unit }}</small></span>
        <span v-if="card.sub" class="metric-sub" :class="card.subTone">{{ card.sub }}</span>
      </div>
      <div v-if="isBidding" class="metric-card highlight">
        <span class="metric-label">Your Bid</span>
        <span class="metric-value">{{ myLiveBids.length ? formatAmountToDollar(myLiveBidTotal, 0, token.currency || 'USD') : 'None yet' }}</span>
        <span class="metric-sub">{{ yourBidSub }}</span>
      </div>
      <div v-else class="metric-card highlight">
        <span class="metric-label">Your Position</span>
        <div class="metric-value-row">
          <img src="@/assets/img/token-icon.svg" alt="" class="token-icon" />
          <span class="metric-value">{{ (tokenBalance || 0).toLocaleString() }} <small>{{ positionUnit }}</small></span>
        </div>
        <span v-if="tokenBalance > 0" class="metric-sub">{{ positionValue }}</span>
      </div>
    </section>

    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <!-- FUNDING PROGRESS -->
    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <!-- No funding bar before a book is priced: units are not minted until then, so there is
         no offer size to measure against. Raised, left and the percentage come from one set of
         figures (trade-view), so they always add up. -->
    <section v-if="showsRaiseProgress && !showsSubscription && !byAvailability" class="progress-section">
      <div class="progress-card">
        <div class="progress-header">
          <div class="progress-stats">
            <div class="progress-raised">
              <span class="progress-amount">{{ formatAmountToDollar(raise.raised, 2, token.currency || 'USD') }}</span>
              <span class="progress-goal">of {{ formatAmountToDollar(raise.goal, 2, token.currency || 'USD') }}</span>
            </div>
            <span class="progress-percent" :class="{ complete: token.completed_raise }">
              {{ token.completed_raise ? 'Complete' : raise.percent.toFixed(1) + '%' }}
            </span>
          </div>
        </div>
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: Math.min(raise.percent, 100) + '%' }"></div>
        </div>
        <div class="progress-footer">
          <div class="progress-meta">
            <span v-if="token.completed_raise">Fully raised</span>
            <template v-else>
              <span>{{ formatAmountToDollar(raise.left, 2, token.currency || 'USD') }} left</span>
              <template v-if="remaining > 0">
                <span class="separator">·</span>
                <span>about {{ Math.round(remaining).toLocaleString('en-US') }} {{ unitWordFor(remaining) }} still on offer</span>
              </template>
            </template>
          </div>
          <div v-if="tradingHalted" class="inactive-badge">
            <svg viewBox="0 0 16 16" fill="none"><path d="M8 5.333V8M8 10.667h.007M14 8A6 6 0 112 8a6 6 0 0112 0z" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>
            Trading paused
          </div>
          <div v-else-if="!token.secondaries && token.completed_raise" class="inactive-badge">
            <svg viewBox="0 0 16 16" fill="none"><path d="M8 5.333V8M8 10.667h.007M14 8A6 6 0 112 8a6 6 0 0112 0z" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>
            Secondary trade inactive
          </div>
        </div>
      </div>
    </section>

    <!-- A book build mints no units until it prices, so "% funded" is meaningless while it
         runs. How far the offer is subscribed is the figure that actually moves. -->
    <section v-else-if="showsSubscription" class="progress-section">
      <div class="progress-card">
        <div class="progress-header">
          <div class="progress-stats">
            <div class="progress-raised">
              <span class="progress-amount">
                {{ formatAmountToDollar(book ? book.summary.total_demand : 0, 2, token.currency || 'USD') }}
              </span>
              <span class="progress-goal">
                bid of {{ formatAmountToDollar(book ? book.offering.raise_amount : token.raise_amount, 2, token.currency || 'USD') }} on offer
              </span>
            </div>
            <span class="progress-percent" :class="{ complete: isOversubscribed }">
              {{ book ? subscribedPercent + '% subscribed' : '—' }}
            </span>
          </div>
        </div>
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: subscriptionBarWidth + '%' }"></div>
        </div>
        <div class="progress-footer">
          <div class="progress-meta">
            <template v-if="isOversubscribed"><span>Oversubscribed</span><span class="separator">·</span></template>
            <template v-else-if="!book"><span>No bids yet</span><span v-if="token.offer_closes_at" class="separator">·</span></template>
            <template v-if="token.offer_closes_at">
              <span>Bidding closes {{ formatOfferMoment(token.offer_closes_at, token.market_timezone) }}</span>
              <span class="separator">·</span>
              <span>the {{ book?.offering?.is_rate_based ? 'rate' : 'share price' }} is set then</span>
            </template>
          </div>
        </div>
      </div>
    </section>

    <!-- Metals and NGX stocks sell at the market price with no goal to reach, so instead of
         "% funded" the card says how much is left to buy and what it is worth today. -->
    <section v-else-if="byAvailability" class="progress-section">
      <div class="progress-card">
        <div class="progress-header">
          <div class="progress-stats">
            <div class="progress-raised">
              <span class="progress-amount">{{ unitsOf(remaining) }}</span>
              <span class="progress-goal">available to buy</span>
            </div>
          </div>
        </div>
        <div class="progress-footer">
          <div class="progress-meta">
            <span>worth {{ formatAmountToDollar(remaining * currentPrice, 2, token.currency || 'USD') }} at today's price</span>
            <template v-if="Number(token.total_raised) > 0">
              <span class="separator">·</span>
              <span>{{ unitsOf(token.total_raised) }} sold so far</span>
            </template>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <!-- PRICE: stocks, funds, metals, and equity once it trades -->
    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <!-- From the nightly price job. Hidden until there are two closes to draw between. -->
    <section v-if="showsPriceChart && closes.length >= 2" class="chart-section">
      <TokenPriceChart
        :closes="closes"
        :currency="token.currency || 'USD'"
        :decimals="priceDecimals"
        :current-value="currentPrice"
        :title="chartTitle"
        :note="chartNote"
        :flat-note="flatNote"
      />
      <RangeBars :rows="rangeRows" :currency="token.currency || 'USD'" :decimals="priceDecimals" />
      <p v-if="isFund && token.interest > 0" class="chart-caption">
        A steady line is the point: the rate builds into the price every day, so there’s no coupon to wait for.
      </p>
    </section>

    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <!-- WHAT THIS KIND OF DEAL NEEDS EXPLAINED, full width under the price -->
    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <!-- A raise at a set price: how it has gone, and where it is in its timeline. -->
    <section v-if="isRaisingEquity" class="board-section">
      <RaiseHistory :token-id="token._id" :currency="token.currency || 'NGN'" />
    </section>
    <section v-if="equitySteps.length" class="board-section">
      <OfferSteps title="Offer Timeline" :steps="equitySteps" />
    </section>
    <!-- An open book: demand at each price, and how the price will be set. -->
    <section v-if="isBidding && bookIsLive" class="board-section">
      <DemandByPrice :book="book" :currency="token.currency || 'NGN'" />
    </section>
    <section v-if="isBidding" class="board-section">
      <OfferSteps title="How This Offer Works" :steps="biddingSteps" />
    </section>
    <!-- A fund: how the rate becomes the return. -->
    <section v-if="isFund" class="board-section">
      <FundEarn :token="token" :holding="tokenBalance" :price="currentPrice" />
    </section>
    <!-- A metal or an NGX stock: what is actually held for the tokens, and what one is. -->
    <!-- Backing card hidden for now. To bring it back, uncomment this and the BackingCard import and registration below.
    <section v-if="kind === 'commodity' || kind === 'listed'" class="board-section">
      <BackingCard :token-id="token._id" :kind="kind === 'listed' ? 'ngx' : 'commodity'" :commodity="commodity" />
    </section>
    -->
    <section v-if="explainer.items.length" class="board-section">
      <PointsList :title="explainer.title" :items="explainer.items" />
    </section>

    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <!-- MAIN CONTENT GRID -->
    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <div class="main-grid">
      <div class="main-column">
        <!-- Why We Listed -->
        <div v-if="token.type === 'Public' && whyWeListed?.link" class="info-card clickable" @click="openWhyWeListed">
          <div class="info-icon">
            <svg viewBox="0 0 20 20" fill="none"><path d="M10 13.333v-3.333M10 6.667h.008M17.5 10a7.5 7.5 0 11-15 0 7.5 7.5 0 0115 0z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
          </div>
          <div class="info-content">
            <span class="info-title">Why we listed this company</span>
            <span class="info-desc">Learn about our due diligence and listing criteria</span>
          </div>
          <svg class="info-arrow" viewBox="0 0 16 16" fill="none"><path d="M6 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </div>

        <!-- Payout Info for Debt/Fixed Interest. Not funds: a fund pays no coupon, its return
             is in the unit price (How You Earn, above). -->
        <div v-if="tokenBalance > 0 && isDebtType" class="payout-card">
          <button type="button" class="payout-header" :aria-expanded="payoutOpen" @click="payoutOpen = !payoutOpen">
            <span class="payout-title">Investment Payout Info</span>
            <svg class="payout-chevron" :class="{ open: payoutOpen }" viewBox="0 0 16 16" fill="none"><path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
          <div v-if="payoutOpen" class="payout-content">
            <div class="payout-row">
              <div class="payout-item">
                <span class="payout-label">Investment Amount</span>
                <span class="payout-value">{{ formatAmountToDollar(tokenBalance * token?.price?.buy, 2, token.currency) }}</span>
              </div>
              <div class="payout-arrow">
                <svg viewBox="0 0 24 24" fill="none"><path d="M12 5v14M12 19l-4-4M12 19l4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </div>
              <div class="payout-item highlight">
                <span class="payout-label">Expected Return</span>
                <span class="payout-value">{{ formatAmountToDollar(tokenPayoutDetails?.totalPayout, 2, token.currency) }}</span>
              </div>
            </div>
            <!-- How far through its term the note is: issued tenor days before maturity. -->
            <div v-if="term" class="term-track">
              <div class="term-bar"><div class="term-fill" :style="{ width: term.percent + '%' }"></div></div>
              <div class="term-labels">
                <span>Issued {{ formatToStringDate(term.start, true) }}</span>
                <span class="term-today">Day {{ term.elapsed }} of {{ term.total }}</span>
                <span>Matures {{ formatToStringDate(term.end, true) }}</span>
              </div>
            </div>
            <button class="breakdown-btn" @click="openInvestModal('payout')">
              <svg viewBox="0 0 16 16" fill="none"><path d="M14 8H8M8 8V2M8 8L2 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              View Full Breakdown
            </button>
          </div>
        </div>

        <!-- Our bids, with what can still be done to them. In the side column on a wide
             screen; here below it, where the side column is hidden. -->
        <section v-if="isBidding && myLiveBids.length" class="content-section below-lg">
          <YourBidCard
            :bids="myLiveBids"
            :currency="token.currency || 'NGN'"
            :rate-based="Boolean(book?.offering?.is_rate_based)"
            :busy="withdrawingBid"
            @change="changeBid"
            @withdraw="withdrawBid"
          />
        </section>

        <!-- What we already have riding on this deal, before anything about the deal itself.
             A second bid is legitimate; one placed because the first was forgotten is not. -->
        <section v-if="myLiveBids.length && !isBidding" class="content-section">
          <div class="existing-bid">
            <div class="existing-bid__text">
              <strong>
                You have {{ myLiveBids.length }} live
                {{ myLiveBids.length === 1 ? 'bid' : 'bids' }} on this offer
              </strong>
              <span>
                {{ formatAmountToDollar(myLiveBidTotal, 2, token.currency || 'USD') }} committed
                <template v-if="myBidLevels">at {{ myBidLevels }}</template>. You can change or
                withdraw {{ myLiveBids.length === 1 ? 'it' : 'them' }} until the offer closes.
              </span>
            </div>
            <router-link class="existing-bid__link" :to="{ name: 'MyInvestments', query: { tab: 'bids' } }">
              View my bids
            </router-link>
          </div>
        </section>

        <!-- How the offer works, then how it actually priced. Both render nothing on a
             continuously filling deal, and an empty section takes no space. -->
        <section class="content-section">
          <OfferTerms :token="token" />
        </section>
        <section class="content-section">
          <OfferingBook :token="token" />
        </section>

        <!-- Metal reference price and attribution; renders nothing for other deals. -->
        <section class="content-section">
          <CommodityInfo headless :token-id="token._id" :closes="closes" :currency="token.currency || 'USD'" :today-change="todayChange" @loaded="commodity = $event" />
        </section>

        <!-- NGX listing figures; renders nothing for a deal with no NGX listing. -->
        <section class="content-section">
          <NgxInfo headless :token-id="token._id" :currency="token.currency || 'NGN'" @loaded="ngx = $event" />
        </section>

        <!-- What the company has filed with the NGX; hides itself when there is nothing to show. -->
        <section v-if="kind === 'listed'" class="content-section">
          <NgxFilings :token-id="token._id" />
        </section>

        <!-- Company Information -->
        <section class="content-section section-card">
          <MemoTable />
        </section>

        <!-- Company Updates -->
        <section v-if="companyUpdate && companyUpdate.length > 0" class="content-section section-card">
          <CompanyUpdateCard />
        </section>
      </div>

      <!-- Sidebar -->
      <aside class="side-column">
        <YourBidCard
          v-if="isBidding && myLiveBids.length"
          :bids="myLiveBids"
          :currency="token.currency || 'NGN'"
          :rate-based="Boolean(book?.offering?.is_rate_based)"
          :busy="withdrawingBid"
          @change="changeBid"
          @withdraw="withdrawBid"
        />

        <!-- What can be bought or sold on the Exchange right now. -->
        <div v-if="!(isBidding && hasOfferPeriodOpen)" class="side-card">
          <h3 class="side-title">Order Availability</h3>
          <div v-if="noOrdersAvailable" class="no-orders-message">
            <svg viewBox="0 0 20 20" fill="none"><path d="M10 6.667v3.333m0 3.333h.008M17.5 10a7.5 7.5 0 11-15 0 7.5 7.5 0 0115 0z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
            <span>No orders currently available to buy or sell</span>
          </div>
          <div v-else class="availability-list">
            <!-- Available to buy comes from sell orders, available to sell from buy orders. -->
            <div class="availability-row">
              <span class="availability-label">Available to Buy</span>
              <span class="availability-value" :class="{ 'has-value': hasSellOrders, 'no-value': !hasSellOrders }">
                <template v-if="hasSellOrders">
                  <span>{{ availabilityMain(totalSellUnits, totalSellAmount) }}</span>
                  <span v-if="byAvailability" class="availability-sub">{{ availabilityMoney(totalSellAmount) }}</span>
                </template>
                <template v-else>None</template>
              </span>
            </div>
            <div class="availability-row">
              <span class="availability-label">Available to Sell</span>
              <span class="availability-value sell-value" :class="{ 'has-value': hasBuyOrders, 'no-value': !hasBuyOrders }">
                <template v-if="hasBuyOrders">
                  <span>{{ availabilityMain(totalBuyUnits, totalBuyAmount) }}</span>
                  <span v-if="byAvailability" class="availability-sub">{{ availabilityMoney(totalBuyAmount) }}</span>
                </template>
                <template v-else>None</template>
              </span>
            </div>
          </div>
        </div>

        <TokenInfoCard
          :token="token"
          :asset="asset"
          :commodity="commodity"
          :ngx="ngx"
          :hide-shares-on-offer="metricCards.some((c) => c.label === 'Shares on Offer')"
        />

        <!-- Invest CTA (Desktop) -->
        <div v-if="tradingHalted" class="inactive-badge">
          {{ tradingHaltedNote }}
        </div>
        <div v-else-if="canInvest && !offerAcceptsMoney" class="offer-shut offer-shut--block">
          {{ offerClosedReason }}
        </div>
        <button v-else-if="canInvest" class="sidebar-cta" @click="openInvestModal('invest')" v-can.disable="'invest:trade'">
          <svg viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
          {{ isBidding ? 'Place a Bid' : primaryCtaLabel }}
        </button>
        <button v-else-if="canTradeSecondaries" class="sidebar-cta secondary" @click="openInvestModal('trade shares')" v-can.disable="'invest:trade'">
          {{ byAvailability ? tradeLabel : 'Trade Shares' }}
        </button>
      </aside>
    </div>

    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <!-- MODALS -->
    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <InvestModal v-if="showInvestModal" :title="modalTitle" :subTitle="investSubTitle" :form="investForm" :bgChange="bgChange" @close="closeModal" @go-back="goBack">
      <template v-slot:content>
        <ListingTokenAmountForm v-if="investForm === 'ListingTokenAmountForm'" class="tw-px-4 lg:tw-px-10" :market="tradeMarket" @completed="completed" />
        <PayoutBreakdown v-if="investForm === 'PayoutBreakdown'" class="tw-px-4 lg:tw-px-10" @completed="completed" />
        <!-- This app's SideModal .modal-content sets no padding, so every form insets itself.
             Kept at px-4 rather than the lg:px-10 the buy form uses: this form carries far more
             in the same panel — two fields, a summary and a four-step explainer — so the wider
             inset squeezes the content rather than framing it. -->
        <CommitmentForm
          v-if="investForm === 'CommitmentForm'"
          class="tw-px-4"
          :amendId="amendCommitmentId"
          @completed="completed"
        />
        <SecondariesTokenAmountForm v-if="investForm === 'SecondariesTokenAmountForm'" @completed="completed" @switch="openInvestModal('modal')" />
        <SecondaryInfoModal v-if="investForm === 'TradingInfoModal'" />
      </template>
    </InvestModal>

    <InfoModal v-if="showInfoModal && token.completed_raise" @close="closeInfoModal">
      <template v-slot:content>
        <SecondaryTradeInfoModal header="You're now in Secondary Markets" subHeader="This means the company has completely raised funds and you're in control of your asset trading" :details="secondaryInfo" type="tradeInfo" @close="closeInfoModal" />
      </template>
    </InfoModal>
  </div>
</template>

<script>
import BackBtn from '@/components/general/BackBtn';
import { HELD_TO_MATURITY_NOTE, isHeldToMaturity } from '@/utils/held-to-maturity';
import { TRADING_HALTED_NOTE, isTradingHalted } from '@/utils/trading-halt';
import InfoModal from '@/components/general/SmallModal';
import InvestModal from '@/components/general/sidemodal/SideModal';
import DealTypeBadge from '@/components/general/DealTypeBadge.vue';
import ShariaBadge from '@/components/general/ShariaBadge.vue';
import MemoTable from '@/components/tables/MemoTable';
import CompanyUpdateCard from '@/components/trade/CompanyUpdateCard';
import SecondaryInfoModal from '@/components/trade/SecondaryInfoModal';
import SecondaryTradeInfoModal from '@/components/trade/SecondaryTradeInfoModal';
import TokenInfoCard from '@/components/trade/TokenInfoCard';
import ListingTokenAmountForm from '@/components/trade/forms/ListingTokenAmountForm';
import PayoutBreakdown from '@/components/trade/forms/PayoutBreakdown';
import SecondariesTokenAmountForm from '@/components/trade/forms/SecondariesTokenAmountForm';
import OfferTerms from '@/components/trade/OfferTerms.vue';
import OfferingBook from '@/components/trade/OfferingBook.vue';
import CommitmentForm from '@/components/trade/forms/CommitmentForm.vue';
import CommodityInfo from '@/components/trade/CommodityInfo.vue';
import NgxInfo from '@/components/trade/NgxInfo.vue';
import NgxFilings from '@/components/trade/NgxFilings.vue';
import TokenPriceChart from '@/components/trade/TokenPriceChart.vue';
import RangeBars from '@/components/trade/RangeBars.vue';
import RaiseHistory from '@/components/trade/RaiseHistory.vue';
import OfferSteps from '@/components/trade/OfferSteps.vue';
import DemandByPrice from '@/components/trade/DemandByPrice.vue';
import YourBidCard from '@/components/trade/YourBidCard.vue';
import FundEarn from '@/components/trade/FundEarn.vue';
// import BackingCard from '@/components/trade/BackingCard.vue'; // hidden for now, see the template
import PointsList from '@/components/trade/PointsList.vue';
import { AuctionPricing, formatOfferMoment, hasMeaningfulRaiseProgress, hasOfferPeriod, hasSettledPricing, isTakingBids, resolveTokenState } from '@/utils/offering';
import { compact, currencySymbol } from '@/utils/exchange.js';
import { DealKinds, closesFrom, dayChange, dealKind, metricCards, neverCharts, priceDecimals, raiseFigures, remainingUnits, sellsByAvailability, showsPriceChart, unitPrice, unitWord, unitsLabel, yearRange } from '@/utils/trade-view';
import { capitalizeEachWord, formatAmountToDollar, formatToStringDate, truncate } from '@/utils/helpers';
import { permissionsService } from '@/utils/permissionChecker';
import responseHandler from '@/utils/responseHandler.js';
import { getItem } from '@/utils/storage';
import api from '@/utils/api';
import { mapActions, mapState } from 'vuex';

export default {
  name: 'TokenTrade',
  components: { MemoTable, CompanyUpdateCard, OfferTerms, OfferingBook, CommitmentForm, InvestModal, InfoModal, TokenInfoCard, SecondaryTradeInfoModal, SecondaryInfoModal, ListingTokenAmountForm, PayoutBreakdown, SecondariesTokenAmountForm, BackBtn, DealTypeBadge, ShariaBadge, CommodityInfo, NgxInfo, NgxFilings, TokenPriceChart, RangeBars, RaiseHistory, OfferSteps, DemandByPrice, YourBidCard, FundEarn, /* BackingCard, */ PointsList },
  data() {
    return {
      showInvestModal: false,
      showInfoModal: null,
      bgChange: false,
      modalTitle: '',
      investForm: '',
      investSubTitle: '',
      orgDetails: {},
      // Aggregate demand for this offering, when it has a book.
      book: null,
      // Daily closes for the price chart, and the metal details on a commodity deal.
      closes: [],
      commodity: null,
      // The NGX listing, when this deal follows one. It is what makes a deal an NGX stock.
      ngx: null,
      // A bid being withdrawn from the Your Bid card.
      withdrawingBid: false,
      // The payout card, open until it is folded away.
      payoutOpen: true,
      // A logo that failed to load shows the deal's initial instead.
      logoFailed: false,
      secondaryInfo: [
        { header: 'Selling Assets', text: 'You can set prices for your already acquired assets and put them up for listing.' },
        { header: 'Buying Assets', text: 'You can also browse through listings and buy more assets to add to your portfolio.' },
      ],
    };
  },
  computed: {
    // What state the offer itself is in, independent of whether we are eligible to bid.
    offerState() {
      return resolveTokenState(this.token || {});
    },
    // "Is the offer taking money at all": a closed book is shut to everybody.
    offerAcceptsMoney() {
      return this.offerState.acceptsMoney;
    },
    // An admin has paused trading in this token; the backend refuses every order, bid and
    // investment for it, so the buttons give way to a notice. Absent flag = not halted.
    tradingHalted() { return isTradingHalted(this.token); },
    tradingHaltedNote() { return TRADING_HALTED_NOTE; },
    offerClosedReason() {
      return this.offerState.detail || this.offerState.label;
    },
    primaryCtaLabel() {
      if (this.byAvailability) return this.tradeLabel;
      return this.offerState.windowed ? 'Place a bid' : 'Invest Now';
    },
    /**
     * Whether the deal takes new money, and whether it trades on the Exchange.
     *
     * No deal-room subscription check here. Subscriptions belong to individual investors (every
     * /user/.../subscription route is user-only), so a company can neither hold one nor read
     * one. Who may buy a private deal is decided by the backend (exchange-purchase
     * assertUserCanPurchasePrivateToken), which refuses with a reason the drawer shows.
     */
    canInvest() {
      return ['Public', 'Private'].includes(this.token?.type) && !this.token?.completed_raise;
    },
    canTradeSecondaries() {
      return Boolean(this.token?.completed_raise && this.token?.secondaries);
    },
    logo() {
      return this.token?.image || this.asset?.logo || '';
    },
    // Set by My Bids when sending someone here to change an existing bid.
    amendCommitmentId() {
      return this.$route.query.amend || '';
    },
    myLiveBids() {
      const all = this.$store.state.TokenModule.commitments?.docs || [];
      return all.filter(
        (c) => c.status === 'SUBMITTED'
          && String(c.token?._id || c.token) === String(this.token?._id)
      );
    },
    /**
     * The level(s) bid at, phrased for the banner.
     *
     * Rate-based deals bid a rate, everything else a price; a strike bid named neither. Several
     * bids at one level read as one level, and genuinely different levels are listed.
     */
    myBidLevels() {
      const rateBased = ['Debt', 'Fixed Interest'].includes(this.token?.investment_type);
      const levels = this.myLiveBids.map((c) => {
        if (c.is_strike_bid) return 'the final ' + (rateBased ? 'rate' : 'price');
        const value = rateBased ? c.bid_rate : c.bid_price;
        if (!(Number(value) > 0)) return '';
        return rateBased
          ? `${value}%`
          : formatAmountToDollar(value, 2, this.token.currency || 'USD');
      }).filter(Boolean);
      return [...new Set(levels)].join(', ');
    },

    // A book build mints no units until it prices, so there is nothing to measure progress
    // against and a 0% bar would misrepresent it.
    subscribedPercent() {
      return (Number(this.book?.summary?.cover_ratio || 0) * 100).toFixed(1);
    },

    isOversubscribed() {
      return this.book?.summary?.subscription_status === 'OVERSUBSCRIBED';
    },

    // Cover can exceed 1, so the bar caps and the label carries the overflow.
    subscriptionBarWidth() {
      return Math.min(Number(this.book?.summary?.cover_ratio || 0) * 100, 100).toFixed(1);
    },

    /**
     * Report subscription rather than funding while the book is still open.
     *
     * Units are not minted until an offering prices, so % funded sits at 0 however strong
     * demand is. Once priced, the units exist and funding is the honest measure again.
     */
    showsSubscription() {
      return hasOfferPeriod(this.token) && !hasSettledPricing(this.token);
    },

    showsRaiseProgress() {
      return hasMeaningfulRaiseProgress(this.token);
    },

    myLiveBidTotal() {
      return this.myLiveBids.reduce((sum, c) => sum + Number(c.amount || 0), 0);
    },
    ...mapState({
      token: (state) => state.TokenModule.tokenInfo,
      asset: (state) => state.TokenModule.assetInfo,
      tokenBalance: (state) => state.TokenModule.tokenBalance,
      tokenPayoutDetails: (state) => state.TokenModule.tokenPayoutDetails,
      heldToMaturity: (state) => isHeldToMaturity(state.TokenModule.tokenPayoutDetails),
      whyWeListed: (state) => state.TokenModule.whyWeListed,
      companyUpdate: (state) => state.TokenModule.tokenUpdates,
      sellOrderBook: (state) => state.TokenModule.sellOrderBook,
      buyOrderBook: (state) => state.TokenModule.buyOrderBook,
    }),
    // A note pays a coupon; a fund's return is in its unit price, so it has no payout card.
    isDebtType() {
      const type = this.token?.investment_type;
      return type === 'Debt' || type === 'Fixed Interest';
    },

    // ── Order availability: open orders on the Exchange ──
    totalSellUnits() { return (this.sellOrderBook || []).reduce((sum, o) => sum + Number(o.amount || 0), 0); },
    totalBuyUnits() { return (this.buyOrderBook || []).reduce((sum, o) => sum + Number(o.amount || 0), 0); },
    totalSellAmount() { return (this.sellOrderBook || []).reduce((sum, o) => sum + Number(o.amount || 0) * Number(o.price || 0), 0); },
    totalBuyAmount() { return (this.buyOrderBook || []).reduce((sum, o) => sum + Number(o.amount || 0) * Number(o.price || 0), 0); },
    hasSellOrders() { return Boolean(this.sellOrderBook?.length); },
    hasBuyOrders() { return Boolean(this.buyOrderBook?.length); },
    noOrdersAvailable() { return !this.hasBuyOrders && !this.hasSellOrders; },

    // ── What this kind of deal shows (utils/trade-view) ──
    kind() { return dealKind(this.token, { ngx: this.ngx }); },
    isFund() { return this.kind === DealKinds.FUND; },
    metricCards() {
      return metricCards(this.token, { book: this.book, closes: this.closes, commodity: this.commodity, ngx: this.ngx });
    },
    currentPrice() { return unitPrice(this.token); },
    todayChange() { return dayChange(this.token); },
    priceDecimals() { return priceDecimals(this.token); },
    positionUnit() { return unitWord(this.token, this.tokenBalance); },
    positionValue() {
      const currency = this.token.currency || 'USD';
      const value = formatAmountToDollar(this.tokenBalance * this.currentPrice, 2, currency);
      if ([DealKinds.FUND, DealKinds.COMMODITY].includes(this.kind)) return `${value} today`;
      if (this.kind === DealKinds.LISTED) return `${value} at the last close`;
      return `${value} at ${formatAmountToDollar(this.currentPrice, this.priceDecimals, currency)}`;
    },
    raise() { return raiseFigures(this.token); },
    byAvailability() { return sellsByAvailability(this.token, { ngx: this.ngx }); },
    remaining() { return remainingUnits(this.token); },
    // Metals and NGX stocks trade at the market price, so their one button is "Trade Silver" /
    // "Trade Shares" and opens the invest drawer with Buy and Sell tabs.
    tradeLabel() {
      return this.kind === DealKinds.COMMODITY ? `Trade ${this.commodity?.name || this.token.name}` : 'Trade Shares';
    },
    // What one token is, for the Buy/Sell drawer on a metal or NGX stock; null otherwise.
    tradeMarket() {
      if (!this.byAvailability) return null;
      if (this.kind === DealKinds.COMMODITY) {
        return {
          kind: 'commodity',
          name: this.commodity?.name || this.token.name,
          unit_label: this.commodity?.unit_label,
          unit_plural: this.commodity?.unit_plural,
          unit_amount: this.commodity?.unit_amount || 1,
        };
      }
      return { kind: 'ngx', name: this.token.name };
    },
    showsPriceChart() { return showsPriceChart(this.token, { ngx: this.ngx }); },
    chartTitle() {
      if (this.kind === DealKinds.FUND) return 'Unit price since launch';
      if (this.kind === DealKinds.LISTED) return 'NGX closing price';
      return `Price per ${unitWord(this.token, 1)}`;
    },
    chartNote() {
      const day = (v) => new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
      if (this.kind === DealKinds.FUND && this.token.interest > 0) {
        return `Grows every day at ${this.token.interest}% a year${this.token.createdAt ? ` · launched ${day(this.token.createdAt)}` : ''}`;
      }
      if (this.kind === DealKinds.LISTED) {
        const at = this.ngx?.quote?.last_updated;
        return at ? `NGX close, ${day(at)} · updates every trading day` : 'Follows the NGX close every trading day';
      }
      if (this.commodity) {
        const source = `${this.nounOf(this.commodity.name)} ${this.commodity.price_basis === 'front_month' ? 'futures' : 'spot'}`;
        const at = this.commodity.quote?.at;
        const when = at ? new Date(at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : null;
        return when ? `Updated ${when} · repriced every weekday from ${source}` : `Repriced every weekday from ${source}`;
      }
      return '';
    },
    // Why an NGX stock's chart can sit flat for weeks.
    flatNote() {
      return this.kind === DealKinds.LISTED ? "NGX only moves a stock's price on days when enough shares trade" : '';
    },

    // ── The trade page by kind of deal (the design boards) ──
    isBidding() { return this.kind === DealKinds.BIDDING; },
    isRaisingEquity() { return this.kind === DealKinds.EQUITY && !this.token.completed_raise; },
    hasOfferPeriodOpen() { return isTakingBids(this.token); },
    bookIsLive() { return Boolean(this.book?.summary?.is_live); },
    showsKindBadge() { return [DealKinds.COMMODITY, DealKinds.LISTED].includes(this.kind); },
    kindBadge() {
      return this.kind === DealKinds.COMMODITY ? { label: 'Commodity', tone: 'commodity' } : { label: 'NGX Stock', tone: 'ngx' };
    },
    // The tag beside the name: what kind of metal or crop, the NGX sector, the fund's category.
    headerTag() {
      if (this.kind === DealKinds.COMMODITY) {
        const groups = { precious: 'Precious metal', base: 'Base metal', energy: 'Energy', grains: 'Grain', softs: 'Soft commodity', livestock: 'Livestock', dairy: 'Dairy', lumber: 'Lumber' };
        return groups[this.commodity?.group] || '';
      }
      if (this.kind === DealKinds.LISTED) return this.ngx?.sector || this.asset?.industry || '';
      if (this.isFund) return this.token.investment_category || this.asset?.industry || '';
      return this.asset?.industry || 'Technology';
    },
    biddingChip() {
      if (!this.isBidding || !this.hasOfferPeriodOpen) return '';
      const ms = new Date(this.token.offer_closes_at).getTime() - Date.now();
      const days = Math.floor(ms / 86400000);
      const when = days >= 2 ? `closes in ${days} days` : days === 1 ? 'closes tomorrow' : 'closes today';
      return `Open for bids · ${when}`;
    },
    yourBidSub() {
      if (!this.myLiveBids.length) return 'held in your wallet until the price is set';
      if (this.myLiveBids.length > 1) return `${this.myLiveBids.length} bids · held in your wallet`;
      const [bid] = this.myLiveBids;
      const currency = this.token.currency || 'USD';
      const level = bid.is_strike_bid
        ? 'at the price set'
        : this.book?.offering?.is_rate_based
          ? `at ${Number(bid.bid_rate)}% or more`
          : `up to ${formatAmountToDollar(bid.bid_price, 2, currency)} a share`;
      return `bid ${level} · held in your wallet`;
    },
    // A raise at a set price, from opening to trading.
    equitySteps() {
      const t = this.token;
      if (this.kind !== DealKinds.EQUITY || (t.completed_raise && t.secondaries)) return [];
      const day = (v) => new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
      const price = formatAmountToDollar(this.currentPrice, this.priceDecimals, t.currency || 'USD');
      const opened = t.offer_opens_at || t.createdAt;
      const done = Boolean(t.completed_raise);
      return [
        { title: 'Offer opened', text: opened ? `${day(opened)} at ${price} a share` : `At ${price} a share`, state: 'done' },
        done
          ? { title: 'Raise complete', text: t.completed_raise_date ? `Closed ${day(t.completed_raise_date)}` : 'Fully raised', state: 'done' }
          : { title: 'Investing now', text: t.offer_closes_at ? `Until ${day(t.offer_closes_at)}, or sooner if fully raised` : 'Until the raise is fully subscribed', state: 'current' },
        { title: 'Shares confirmed', text: 'Your shares are final once the offer closes', state: done ? 'done' : 'next' },
        { title: 'Trading opens', text: 'Buy and sell on the Exchange once the raise settles', state: 'next' },
      ];
    },
    // A book-built offer or an auction: bid, one price, allotment, trading.
    biddingSteps() {
      const t = this.token;
      const rate = Boolean(this.book?.offering?.is_rate_based);
      const open = this.hasOfferPeriodOpen;
      const settled = hasSettledPricing(t);
      const payYourBid = t.auction_pricing === AuctionPricing.MULTIPLE;
      const raise = `${currencySymbol(t.currency || 'USD')}${compact(Number(t.raise_amount) || 0)}`;
      const close = t.offer_closes_at ? new Date(t.offer_closes_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : null;
      return [
        { title: close ? `Bid until ${close}` : 'Bid', text: rate ? 'Say how much, and the lowest rate you’ll accept.' : 'Say how much, and the most you’ll pay per share.', state: open ? 'current' : 'done' },
        {
          title: payYourBid ? 'Winning bids pay their own price' : rate ? 'One rate for all' : 'One price for all',
          text: payYourBid ? `The cut-off is set where demand meets the ${raise} raise.` : `Set where demand meets the ${raise} raise.`,
          state: settled ? 'done' : open ? 'next' : 'current',
        },
        { title: 'Shares allotted', text: rate ? 'Bids at or below the rate are allotted; the rest is released.' : 'Bids at or above the price get shares; the rest is released.', state: settled ? 'done' : 'next' },
        { title: 'Trade later', text: 'Sell on the Exchange once the raise settles.', state: settled && t.secondaries ? 'done' : 'next' },
      ];
    },
    // What one token is (a commodity), or how an NGX listing works.
    explainer() {
      if (this.kind === DealKinds.COMMODITY && this.commodity?.token_is) {
        const holder = this.token.creator?.name;
        return {
          title: 'What You Own',
          items: [
            {
              title: `${this.commodity.token_is} of ${this.nounOf(this.commodity.name)} per token`,
              text: holder ? `Bought and owned by ${holder} for its investors, and held by its custodian.` : 'Bought and held by a custodian for the deal’s investors.',
            },
            { title: 'Your return is the price', text: 'No dividends or interest. Sell any weekday at the market price.' },
          ],
        };
      }
      if (this.kind === DealKinds.LISTED) {
        return {
          title: 'How This Listing Works',
          items: [
            { title: 'A real share for every token', text: 'Bought on the NGX after you pay, and held for token holders.' },
            { title: 'Priced at the NGX close', text: 'Every trading day. A move over 10% in a day, such as after a bonus issue, is reviewed first.' },
          ],
        };
      }
      return { title: '', items: [] };
    },
    // Where today's price sits: the day's and the year's range for a stock, the year's for a metal
    // (from the token's own closes, so in the deal's currency and per token).
    rangeRows() {
      const price = this.currentPrice;
      if (this.kind === DealKinds.LISTED) {
        const q = this.ngx?.quote || {};
        return [
          q.day_low && q.day_high && { label: 'Today’s range', low: q.day_low, high: q.day_high, current: price },
          q.low52wk && q.high52wk && { label: '52-week range', low: q.low52wk, high: q.high52wk, current: price },
        ].filter(Boolean);
      }
      if (this.kind === DealKinds.COMMODITY) {
        const range = yearRange(this.closes);
        return range ? [{ label: `52-week range, per ${unitWord(this.token, 1)}`, low: range.low, high: range.high, current: price }] : [];
      }
      return [];
    },
    // A note's term: it was issued tenor days before it matures.
    term() {
      const end = this.token?.maturity ? new Date(this.token.maturity) : null;
      const total = Number(this.token?.tenor);
      if (!end || Number.isNaN(end.getTime()) || !(total > 1)) return null;
      const start = new Date(end.getTime() - total * 86400000);
      const elapsed = Math.min(Math.max(Math.floor((Date.now() - start.getTime()) / 86400000), 0), total);
      return { start, end, total, elapsed, percent: (elapsed / total) * 100 };
    },
  },
  created() { this.getTradeDetails(); },
  mounted() {
    // Arriving with ?amend= means the bid to change was already chosen in My Bids.
    if (this.amendCommitmentId) this.openInvestModal('invest');
  },

  watch: {
    /**
     * Offering data loads on the token, not with the page.
     *
     * created() starts an async fetch, so at mounted() the token is still {} and every
     * windowed check reads false — which is why the live-bid banner and the book never
     * appeared. Keying off the loaded token instead of a lifecycle hook fixes both.
     */
    'token._id': {
      immediate: true,
      handler(id) {
        if (!id) return;
        this.logoFailed = false;
        this.loadMarketData(id);
        if (!hasOfferPeriod(this.token)) return;
        this.$store.dispatch('TokenModule/getCommitments', { limit: 100 });
        this.loadBook();
      },
    },
  },

  methods: {
    ...mapActions(['setLoading']),
    ...mapActions('TokenModule', ['getToken', 'getTokenBalance', 'getAssetInfo', 'getTokenUpdates', 'getWhyWeListed', 'getTokenOrders', 'fetchOfferingBook']),
    ...mapActions('organisationModule', ['getWalletBalance', 'getCurrencyWallets']),
    formatAmountToDollar, capitalizeEachWord, truncate, formatToStringDate,
    // Exposed because the subscription card renders the close moment.
    formatOfferMoment,
    hasOfferPeriod,

    // Price history for the chart and the stock cards. An extra: a failure leaves them out.
    // Metal and NGX details arrive from CommodityInfo and NgxInfo, which already load them.
    async loadMarketData(id) {
      this.closes = [];
      this.commodity = null;
      this.ngx = null;
      if (neverCharts(this.token)) return;
      try {
        const res = await api.getTokenOhlvc(id);
        if (this.token?._id === id) this.closes = closesFrom(res?.data?.data);
      } catch (e) {
        this.closes = [];
      }
    },

    // Tokens in what they stand for: grams of a metal (from its details), shares otherwise.
    unitsOf(units) {
      return unitsLabel(this.token, units, { market: this.commodity });
    },

    // "silver", but "WTI crude oil": a name mid-sentence keeps its acronyms.
    nounOf(name) {
      return String(name || '').split(' ').map((word) => (/^[A-Z]{2,}$/.test(word) ? word : word.toLowerCase())).join(' ');
    },
    unitWordFor(units) { return unitWord(this.token, units); },
    // Units first for what is bought by the unit (metals, NGX shares), with the money under them; money otherwise.
    availabilityMain(units, amount) {
      return this.byAvailability ? this.unitsOf(units) : this.availabilityMoney(amount);
    },
    availabilityMoney(amount) {
      return formatAmountToDollar(amount, 2, this.token.currency || 'USD');
    },

    async loadBook() {
      this.book = await this.fetchOfferingBook({ id: this.token._id });
    },

    // The bid form, prefilled with this bid, as My Bids does. Changing a bid spends treasury
    // money, so it carries the same permission as placing one.
    changeBid(bid) {
      if (!permissionsService.hasPermission('invest:trade')) return;
      this.$router.replace({ query: { ...this.$route.query, amend: bid._id } }).then(() => this.openInvestModal('invest'));
    },
    async withdrawBid(bid) {
      if (!permissionsService.hasPermission('invest:trade')) return;
      this.withdrawingBid = true;
      try {
        const response = await this.$store.dispatch('TokenModule/withdrawCommitment', bid._id);
        if (!response) return;
        this.$store.dispatch('showToast', { description: 'Your bid has been withdrawn and the held funds released.', display: true, type: 'success' });
        // The hold is released, so the bids, the book and the balances have all moved.
        this.$store.dispatch('TokenModule/getCommitments', { limit: 100 });
        this.loadBook();
        this.getWalletBalance();
        this.getCurrencyWallets();
      } finally {
        this.withdrawingBid = false;
      }
    },

    completed() {
      this.showInvestModal = false;
      this.getTradeDetails();
      // A new bid changes the live-bid banner and moves cash into offer_hold, and neither
      // comes from getTradeDetails — without these the page still shows the pre-bid state.
      if (this.offerState.windowed) {
        this.$store.dispatch('TokenModule/getCommitments', { limit: 100 });
        this.loadBook();
      }
      this.getWalletBalance();
      this.getCurrencyWallets();
    },
    async getTradeDetails() {
      this.orgDetails = getItem('org_details');
      this.setLoading(true);
      const id = this.$route.params.id;
      const symbol = this.$route.params.symbol;
      try {
        await Promise.all([this.getToken(id), this.getTokenBalance(symbol), this.getWalletBalance(), this.getWhyWeListed(id)]);
        this.getAssetInfo(this.token.asset);
        this.getTokenUpdates({ id });
        // Open orders on the Exchange, for Order Availability.
        this.getTokenOrders({ id, data: { page: 1, type: 'Buy', limit: 10, status: 'Active' } });
        this.getTokenOrders({ id, data: { page: 1, type: 'Sell', limit: 10, status: 'Active' } });
        this.setLoading(false);
      } catch (error) {
        this.setLoading(false);
        responseHandler.handleError(error);
      }
    },
    closeModal() { this.showInvestModal = false; },
    goBack(modal) {
      if (modal === 'TradingInfoModal') {
        this.modalTitle = `Invest in ${this.token.name}`;
        this.investForm = 'SecondariesTokenAmountForm';
        this.bgChange = true;
      }
    },
    openWhyWeListed() { window.open(this.whyWeListed.link, '_blank'); },
    // Metals and NGX stocks: the invest drawer, with Buy and Sell tabs, in the light theme.
    openTradeDrawer() {
      this.showInvestModal = true;
      this.modalTitle = this.tradeLabel;
      this.investForm = 'ListingTokenAmountForm';
      this.bgChange = false;
    },
    openInvestModal(btn) {
      // Everything but the read-only panels moves money; none of it is allowed while halted.
      if (this.tradingHalted && btn !== 'payout' && btn !== 'modal') {
        this.$store.dispatch('showToast', { description: TRADING_HALTED_NOTE, display: true, type: 'error' });
        return;
      }
      // Metals and NGX stocks trade at the market price from the start, buy or sell.
      if ((btn === 'invest' || btn === 'trade shares') && this.byAvailability) {
        this.openTradeDrawer();
        return;
      }
      // Monthly payouts are held to maturity; the backend refuses these trades too.
      if (btn === 'trade shares' && this.heldToMaturity) {
        this.$store.dispatch('showToast', { description: HELD_TO_MATURITY_NOTE, display: true, type: 'error' });
        return;
      }
      this.showInvestModal = true;
      switch (btn) {
        case 'invest':
          // A windowed offering takes bids, not purchases: different form, different
          // promise. Everything without an offer period keeps the legacy buy flow.
          this.modalTitle = this.offerState.windowed
            ? `Bid for ${this.token.name}`
            : `Invest in ${this.token.name}`;
          this.investForm = this.offerState.windowed
            ? 'CommitmentForm'
            : 'ListingTokenAmountForm';
          this.bgChange = false;
          break;
        case 'payout':
          this.modalTitle = 'Investment Breakdown';
          this.investForm = 'PayoutBreakdown';
          this.bgChange = true;
          break;
        case 'trade shares':
          this.modalTitle = `Invest in ${this.token.name}`;
          this.investForm = 'SecondariesTokenAmountForm';
          this.bgChange = true;
          break;
        case 'modal':
          this.modalTitle = 'Trading Info';
          this.investForm = 'TradingInfoModal';
          this.bgChange = false;
          break;
      }
    },
    closeInfoModal() { this.showInfoModal = false; },
  },
};
</script>

<style lang="scss" scoped>
// ═══════════════════════════════════════════════════════════════════════════
// DESIGN TOKENS (as investor-app's trade page)
// ═══════════════════════════════════════════════════════════════════════════
$bg-primary: #F8FAFC;
$bg-card: #FFFFFF;
$bg-card-hover: #F1F5F9;
$border-color: #E2E8F0;
$text-primary: #0F172A;
$text-secondary: #475569;
$text-muted: #94A3B8;
$accent: #4D84FF;
$btn-primary: #001240;
$btn-primary-hover: #06206B;
$success: #10B981;
$warning: #F59E0B;
$error: #EF4444;

// ═══════════════════════════════════════════════════════════════════════════
// BASE
// ═══════════════════════════════════════════════════════════════════════════
#token-trade-layout {
  background: $bg-primary;
  min-height: 100vh;
  padding: 16px 16px 48px;
  max-width: 1280px;
  margin: 0 auto;
  @media (min-width: 768px) { padding: 20px 24px 48px; }
}

// ═══════════════════════════════════════════════════════════════════════════
// NAVIGATION
// ═══════════════════════════════════════════════════════════════════════════
.nav-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 0;
  border-bottom: 1px solid $border-color;
  margin-bottom: 20px;
}
.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: $text-muted;
}
.breadcrumb-link {
  color: $text-secondary;
  text-decoration: none;
  &:hover { color: $accent; }
}
.breadcrumb-sep { color: $text-muted; }
.breadcrumb-current { color: $text-primary; font-weight: 400; }

// ═══════════════════════════════════════════════════════════════════════════
// HERO
// ═══════════════════════════════════════════════════════════════════════════
.hero-section { margin-bottom: 16px; }
.hero-card {
  background: $bg-card;
  border: 1px solid $border-color;
  border-radius: 10px;
  padding: 20px;
  @media (min-width: 768px) { padding: 24px; }
}
.hero-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  @media (min-width: 768px) { flex-direction: row; justify-content: space-between; align-items: center; }
}
.company-identity {
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
}
.company-avatar {
  width: 56px;
  height: 56px;
  border-radius: 10px;
  background: linear-gradient(135deg, $btn-primary 0%, $btn-primary-hover 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 400;
  flex-shrink: 0;
}
.company-logo {
  width: 56px;
  height: 56px;
  border-radius: 10px;
  object-fit: cover;
  flex-shrink: 0;
}
.company-info { min-width: 0; }
.company-name {
  font-size: 22px;
  font-weight: 400;
  color: $text-primary;
  margin: 0 0 8px;
  @media (min-width: 768px) { font-size: 26px; }
}
.company-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}
.industry-badge {
  padding: 4px 10px;
  background: rgba($btn-primary, 0.08);
  color: $btn-primary;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 400;
}
.bidding-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  color: #047857;
  background: rgba($success, 0.12);
  i { width: 6px; height: 6px; border-radius: 50%; background: $success; }
}
.country-tag {
  display: flex;
  align-items: center;
  gap: 4px;
  color: $text-secondary;
  font-size: 13px;
  svg { width: 14px; height: 14px; }
}
.social-links {
  display: flex;
  gap: 6px;
}
.social-link {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: $bg-primary;
  border-radius: 4px;
  color: $text-secondary;
  transition: background-color 0.2s, color 0.2s;
  svg { width: 16px; height: 16px; }
  &:hover { background: $btn-primary; color: white; }
}
.hero-actions { flex-shrink: 0; }

.invest-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  transition: background-color 0.2s;
  svg { width: 16px; height: 16px; }
  &.primary {
    background: $btn-primary;
    color: white;
    &:hover { background: $btn-primary-hover; }
  }
  &.secondary {
    background: white;
    color: $btn-primary;
    border: 1px solid $btn-primary;
    &:hover { background: rgba($btn-primary, 0.05); }
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

// Stands where the CTA would be when the offer is not taking money. Deliberately not a
// disabled button: a greyed-out control invites clicking to find out why, where a sentence
// answers it outright.
.offer-shut {
  display: inline-flex;
  align-items: center;
  padding: 12px 20px;
  border: 1px dashed #cbd5e1;
  border-radius: 4px;
  background: #f8fafc;
  color: #475569;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;

  &--block {
    display: flex;
    justify-content: center;
    width: 100%;
    white-space: normal;
    text-align: center;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// METRICS BAR
// ═══════════════════════════════════════════════════════════════════════════
.metrics-bar {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 16px;
  @media (min-width: 768px) { grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); }
}
.metric-card {
  position: relative;
  background: $bg-card;
  border: 1px solid $border-color;
  border-radius: 10px;
  padding: 16px;
  &.highlight {
    background: rgba($accent, 0.05);
    border-color: rgba($accent, 0.2);
  }
}
.metric-label {
  display: block;
  font-size: 12px;
  color: $text-muted;
  margin-bottom: 6px;
}
.metric-value {
  font-size: 20px;
  font-weight: 400;
  color: $text-primary;
  &.accent, &.up { color: $success; }
  &.down { color: $error; }
  small { font-size: 12px; color: $text-secondary; }
}
.metric-sub {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: $text-muted;
  &.up { color: $success; }
  &.down { color: $error; }
}
.metric-value-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.token-icon { width: 24px; height: 24px; }

// ═══════════════════════════════════════════════════════════════════════════
// PROGRESS, PRICE AND THE PER-KIND BOARDS
// ═══════════════════════════════════════════════════════════════════════════
.progress-section { margin-bottom: 20px; }
.chart-section { margin-bottom: 20px; }
// The per-kind sections under the price (raise history, timeline, demand, backing): full width.
.board-section { margin-bottom: 20px; }
.chart-caption {
  margin: 10px 0 0;
  font-size: 12px;
  color: $text-secondary;
}
.progress-card {
  background: $bg-card;
  border: 1px solid $border-color;
  border-radius: 10px;
  padding: 20px;
}
.progress-header { margin-bottom: 12px; }
.progress-stats {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
}
.progress-raised {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px;
}
.progress-amount {
  font-size: 18px;
  font-weight: 400;
  color: $text-primary;
}
.progress-goal {
  font-size: 14px;
  color: $text-muted;
}
.progress-percent {
  font-size: 14px;
  font-weight: 400;
  color: $accent;
  &.complete { color: $success; }
}
.progress-bar {
  height: 6px;
  background: #E5EDFF;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 12px;
}
.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, $success 0%, #34D399 100%);
  border-radius: 4px;
  transition: width 0.6s ease;
}
.progress-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.progress-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 13px;
  color: $text-secondary;
  .separator { color: $text-muted; }
}
.inactive-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #FEF3C7;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 400;
  color: #92400E;
  svg { width: 14px; height: 14px; color: $warning; }
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN GRID
// ═══════════════════════════════════════════════════════════════════════════
.main-grid {
  display: grid;
  gap: 16px;
  @media (min-width: 1024px) { grid-template-columns: minmax(0, 1fr) 320px; gap: 20px; }
}
.main-column {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}
.side-column {
  display: flex;
  flex-direction: column;
  gap: 12px;
  @media (max-width: 1023px) { display: none; }
}
// The Your Bid card sits in the side column on a wide screen, and here where that is hidden.
.below-lg {
  @media (min-width: 1024px) { display: none; }
}

// ═══════════════════════════════════════════════════════════════════════════
// INFO CARD
// ═══════════════════════════════════════════════════════════════════════════
.info-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: $bg-card;
  border: 1px solid $border-color;
  border-radius: 10px;
  padding: 16px;
  transition: border-color 0.2s, background-color 0.2s;
  &.clickable {
    cursor: pointer;
    &:hover { border-color: $accent; background: rgba($accent, 0.02); .info-arrow { transform: translateX(4px); } }
  }
}
.info-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba($accent, 0.1);
  border-radius: 7px;
  flex-shrink: 0;
  svg { width: 20px; height: 20px; color: $accent; }
}
.info-content { flex: 1; }
.info-title {
  display: block;
  font-size: 14px;
  font-weight: 400;
  color: $text-primary;
  margin-bottom: 2px;
}
.info-desc {
  display: block;
  font-size: 13px;
  color: $text-secondary;
}
.info-arrow {
  width: 16px;
  height: 16px;
  color: $text-muted;
  transition: transform 0.2s;
}

// ═══════════════════════════════════════════════════════════════════════════
// PAYOUT CARD
// ═══════════════════════════════════════════════════════════════════════════
.payout-card {
  background: $bg-card;
  border: 1px solid $border-color;
  border-radius: 10px;
  overflow: hidden;
}
.payout-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 16px 20px;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
}
.payout-title {
  font-size: 15px;
  font-weight: 400;
  color: $text-primary;
}
.payout-chevron {
  width: 16px;
  height: 16px;
  color: $text-muted;
  transition: transform 0.2s;
  &.open { transform: rotate(180deg); }
}
.payout-content { padding: 4px 20px 20px; }
.payout-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  @media (max-width: 640px) { flex-direction: column; text-align: center; }
}
.payout-item { flex: 1; }
.payout-label {
  display: block;
  font-size: 12px;
  color: $text-muted;
  margin-bottom: 4px;
}
.payout-value {
  font-size: 18px;
  font-weight: 400;
  color: $text-primary;
}
.payout-item.highlight { text-align: right; @media (max-width: 640px) { text-align: center; } }
.payout-item.highlight .payout-value { color: $success; }
.payout-arrow {
  svg { width: 24px; height: 24px; color: $text-muted; }
  @media (min-width: 641px) { transform: rotate(-90deg); }
}
.term-track {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}
.term-bar {
  height: 6px;
  border-radius: 3px;
  background: $bg-card-hover;
  overflow: hidden;
}
.term-fill {
  height: 100%;
  border-radius: 3px;
  background: $success;
}
.term-labels {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 12px;
  color: $text-muted;
}
.term-today { color: $text-secondary; }
.breakdown-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px;
  background: $bg-primary;
  border: 1px solid $border-color;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 400;
  color: $text-primary;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s;
  svg { width: 16px; height: 16px; }
  &:hover { border-color: $accent; color: $accent; }
}

// ═══════════════════════════════════════════════════════════════════════════
// CONTENT SECTIONS
// ═══════════════════════════════════════════════════════════════════════════
.content-section { position: relative; }
// Sections whose component renders nothing for this deal (offer terms, the book, metal
// details) would still take a gap in the column and push it below the side cards.
.content-section:empty { display: none; }
// The company memo and updates draw no frame of their own in this app.
.section-card {
  background: $bg-card;
  border: 1px solid $border-color;
  border-radius: 10px;
  overflow: hidden;
}

.existing-bid {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  padding: 14px 16px;
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  border-radius: 10px;

  &__text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 13px;
    color: #475569;

    strong {
      color: #1e3a8a;
      font-weight: 600;
    }
  }

  &__link {
    font-size: 13px;
    font-weight: 500;
    color: #1d4ed8;
    white-space: nowrap;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// SIDE CARDS
// ═══════════════════════════════════════════════════════════════════════════
.side-card {
  background: $bg-card;
  border: 1px solid $border-color;
  border-radius: 10px;
  padding: 20px;
}
.side-title {
  font-size: 15px;
  font-weight: 400;
  color: $text-primary;
  margin: 0 0 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid $border-color;
}
.availability-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 10px 0;
  border-bottom: 1px solid $border-color;
  &:last-child { border-bottom: none; }
}
.availability-label {
  font-size: 13px;
  color: $text-secondary;
  white-space: nowrap;
}
.availability-value {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  text-align: right;
  font-size: 13px;
  font-weight: 500;
  .availability-sub {
    font-size: 12px;
    font-weight: 400;
    color: $text-muted;
  }
  &.has-value { color: $success; }
  &.sell-value.has-value { color: #F57C00; }
  &.no-value {
    color: $text-muted;
    font-weight: 400;
  }
}
.no-orders-message {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 12px;
  background: rgba($text-muted, 0.08);
  border-radius: 6px;
  font-size: 13px;
  color: $text-secondary;
  line-height: 1.4;
  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    color: $text-muted;
  }
}

.sidebar-cta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px;
  background: $btn-primary;
  border: none;
  border-radius: 4px;
  color: white;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  transition: background-color 0.2s;
  svg { width: 16px; height: 16px; }
  &:hover { background: $btn-primary-hover; }
  &.secondary {
    background: white;
    color: $btn-primary;
    border: 1px solid $btn-primary;
    &:hover { background: rgba($btn-primary, 0.05); }
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}
</style>
